/**
 * Pieces shared by the Market making page, its quote builder and its hedge panel
 * (docs/market-making-ux.md): the data-source contract (so /dev/market-making can render the
 * same screen from mocks), money formatting per currency, the bid / contracts controls, the
 * real-money confirm list and the per-leg placement result.
 */
import { useEffect, useState, type ReactNode } from "react";
import type { Contest, League, LiquidityPosition, Market, Opportunity } from "@greenbook/client";
import type { BuyLeg, BuyResponse, CancelResponse, MoneyMode, OrderSheetClient } from "@openmarketsai/order-sheet";
import type { Ccy } from "../../lib/portfolio.js";
import type { LegRef, MmEntry, PlacedLeg } from "../../lib/marketMaking.js";
import type { MmOffer, MmTier } from "../../lib/mmPlan.js";
import type { EnsuredModel } from "../../lib/mmModel.js";
import type { PurchaseResult } from "@openmarketsai/app-sdk";
import { fmtMoney, fmtPrice } from "../../lib/format.js";
import { useOddsFormat } from "../../lib/odds.js";
import { matchupSep, teamLabel, teamsOf } from "../../lib/marketsBoard.js";

/* ===================================================================== source */

export interface OppState {
  data: Opportunity | undefined;
  isLoading: boolean;
  isError: boolean;
  /** 404: not on this account (another user's id in this browser) or gone. */
  notFound: boolean;
  error: unknown;
  refetch: () => void;
}

/** Can this session place orders from here, and how. */
export interface OrderAccess {
  /** `ok`: an order client exists. `signed_out` / `no_session` (a pasted key can't use the order SDK). */
  client: "ok" | "signed_out" | "no_session";
  /** The session holds trade:execute (needed for Real). */
  canTradeReal: boolean;
  /** Open the "unlock order placement" flow. */
  unlockReal: () => void;
  /**
   * The order SDK client the single-order actions (Get out, Move bid, the custom hedge) open
   * their order sheets on, with `opportunityId` so the order joins the opportunity. Null unless
   * `client` is `ok`. Live: lib/orderClient.ts; the dev preview: an in-memory fake API.
   */
  om: OrderSheetClient | null;
  /**
   * Place quote: ONE headless multi-leg `om.buy`. `opportunityId` joins an existing opportunity;
   * `modelId` files the orders under the Market making model (on a re-quote: the opportunity's
   * own model, or none when it has none). Invalidates the account caches after.
   */
  buy: (legs: BuyLeg[], opts?: { opportunityId?: string; modelId?: string }) => Promise<BuyResponse>;
  cancel: (routerOrderIds: string[]) => Promise<CancelResponse>;
  /** A sheet placed (or may have placed) an order: refresh the account caches. */
  afterOrder: () => void;
}

export interface MmPlan {
  tier: MmTier;
  /** Holdings still loading (the tier reads Free meanwhile; don't upsell yet). */
  loading: boolean;
  /** Holdings or products couldn't be read. */
  error: string | null;
  offers: MmOffer[];
  /** Opens OpenMarkets' checkout; resolves with the outcome (never throws for a user's choice). */
  purchase(code: string): Promise<PurchaseResult>;
}

/**
 * Everything the screen reads. Each `use*` is a hook, called unconditionally in the same order
 * on every render; the object itself must be stable for the screen's lifetime.
 */
/** The server's list of Market making quotes (the model's opportunities). */
export interface ServerQuotes {
  /** Undefined until the model and its list have loaded (or when there's no model yet: []). */
  items: Opportunity[] | undefined;
  /** The model is known and, when there is one, its list has answered (or failed). */
  settled: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

export interface MarketMakingSource {
  /** The local registry: a cache + enrichment of quotes placed in this browser, and the legacy (pre-model) quotes. */
  useRegistry(): MmEntry[];
  register(entry: MmEntry): void;
  addLegs(opportunityId: string, legs: Record<string, LegRef>): void;
  /** Remove a card: forget its local entry and hide it here (a server-listed quote can't be deleted). */
  forget(opportunityId: string): void;
  /** What Remove hid in this browser. */
  useHidden(): ReadonlySet<string>;
  /** Every opportunity filed under the Market making model (any device). Reads only: never creates the model. */
  useServerQuotes(): ServerQuotes;
  /** The model to file a NEW quote under, created if the account has none. Only when the user places a quote. */
  ensureModel(): Promise<EnsuredModel>;
  /** Each local-only opportunity, read by id; polls every 10 s while it has resting legs. */
  useOpportunities(ids: string[]): (id: string) => OppState;
  /** Current prices for the two sides of a card (trimmed liquidity), for consensus and the hedge. */
  useSidePrices(contestId: string, hashes: string[]): LiquidityPosition[] | undefined;
  /** Venue scope: Real → connected venues (a set, maybe empty) and the venues taking limits; Paper → null. */
  useScope(mode: MoneyMode): { allowed: ReadonlySet<string> | null; limitVenues: ReadonlySet<string> | null; loading: boolean };
  /** Open games for the builder's picker (server-side title search from 2 characters). */
  useGames(q: string): { items: Contest[]; isLoading: boolean; isError: boolean; error: unknown; refetch: () => void };
  useContest(id: string | undefined): Contest | undefined;
  /** League by id, for the picker's league pills and chips. */
  useLeagues(): (id: string | undefined) => League | undefined;
  /** One game's full liquidity + market definitions, for the builder. */
  useGameBook(contestId: string | undefined): {
    positions: LiquidityPosition[] | undefined;
    markets: Market[] | undefined;
    isLoading: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => void;
  };
  useOrders(): OrderAccess;
  /** The armed trading mode for orders (Real only with trade:execute; see orderClient.ts). */
  useMode(): { mode: MoneyMode; wantsReal: boolean; setMode: (m: MoneyMode) => void };
  /** The market making plan (in-app purchase): tier, upgrades on offer, and checkout. */
  usePlan(): MmPlan;
  /** The first-time walkthrough's "seen" flag (localStorage live; in memory in the dev preview). */
  readTourSeen(): boolean;
  writeTourSeen(seen: boolean): void;
}

/* ===================================================================== format */

export const ccyOf = (mode: MoneyMode): Ccy => (mode === "real" ? "USD" : "ATLAS");
export const modeOf = (ccy: Ccy): MoneyMode => (ccy === "USD" ? "real" : "paper");

/** USD as $1,234.56; ATLAS whole when it is whole, else 2 decimals (a quote's margin is often under 1 ATLAS). */
export const money = (n: number, ccy: Ccy): string => {
  if (ccy !== "ATLAS") return fmtMoney(n);
  const d = Number.isInteger(Math.round(n * 100) / 100) ? 0 : 2;
  return `${n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })} ATLAS`;
};

/** A whole-cent bid in the user's odds format (the same format as every market price). */
export const fmtBid = (cents: number, format: ReturnType<typeof useOddsFormat>["format"]): string => fmtPrice(cents / 100, null, format);

export const signedMoney = (n: number, ccy: Ccy): string => {
  const sign = n > 0.005 ? "+" : n < -0.005 ? "−" : "";
  return `${sign}${money(Math.abs(n), ccy)}`;
};

export const toneOf = (n: number): string => (n > 0.005 ? "text-up" : n < -0.005 ? "text-down" : "text-dim");

/** Contracts: two decimals under 100, whole above. */
export const fmtContracts = (n: number): string =>
  n.toLocaleString(undefined, { maximumFractionDigits: n < 100 ? 2 : 0 });

/** "Hawks @ Heat" for a head-to-head game, else the contest title. */
export function gameLabel(c: Contest | undefined, fallback = "…"): string {
  if (!c) return fallback;
  const t = teamsOf(c);
  return t ? `${teamLabel(t.away)} ${matchupSep(c)} ${teamLabel(t.home)}` : (c.title ?? fallback);
}

/** Parse typed contracts: a whole number from 1 to 100,000. Null when invalid. */
export function parseContracts(text: string): number | null {
  const n = Number(text.replace(/[,\s]/g, ""));
  return Number.isInteger(n) && n >= 1 && n <= 100_000 ? n : null;
}

/* ================================================================== controls */

export const btnBase =
  "relative inline-flex min-h-11 items-center justify-center rounded-full border px-3.5 text-[12px] font-medium transition-colors duration-150 motion-reduce:transition-none disabled:cursor-not-allowed disabled:opacity-40 lg:min-h-8";
export const btnIdle = "border-border-2 bg-panel text-dim enabled:hover:border-amber/40 enabled:hover:text-ink";
export const btnOn = "border-amber/70 bg-amber/10 text-amber";
export const btnPrimary = "border-amber/60 bg-amber/15 text-amber enabled:hover:bg-amber/25";
export const btnDanger = "border-border-2 bg-panel text-dim enabled:hover:border-down/50 enabled:hover:text-down";

/**
 * Local text for a numeric input: typing isn't fought by the parse/clamp, the value commits
 * whenever the text is valid, and an outside change (the global Size, a stepper) shows through.
 */
function useNumberText(value: number, parse: (t: string) => number | null) {
  const [text, setText] = useState(String(value));
  useEffect(() => {
    setText((t) => (parse(t) === value ? t : String(value)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return [text, setText] as const;
}

const parseWholeCents = (t: string): number | null => {
  const n = Number(t.trim());
  return t.trim() !== "" && Number.isInteger(n) ? n : null;
};

/**
 * ‹ 52¢ › — a 1¢ stepper around a whole-cent bid (2–98¢; the caller clamps). The price shows in
 * the user's odds format; in cents it's also typeable.
 */
export function CentsStepper({
  cents,
  onChange,
  disabled,
  label,
  compact = false,
}: {
  cents: number;
  onChange: (c: number) => void;
  disabled?: boolean;
  label: string;
  compact?: boolean;
}) {
  const { format } = useOddsFormat();
  const [text, setText] = useNumberText(cents, parseWholeCents);
  const step = `grid h-11 w-8 shrink-0 place-items-center rounded-md text-dim enabled:hover:bg-raised enabled:hover:text-ink disabled:opacity-40 ${compact ? "lg:h-7 lg:w-6" : "lg:h-8 lg:w-7"}`;
  const size = compact ? "text-[13px]" : "text-[15px]";
  return (
    <div className="inline-flex items-center rounded-md border border-border-2 bg-bg">
      <button type="button" className={step} disabled={disabled || cents <= 2} onClick={() => onChange(cents - 1)} aria-label={`${label}: 1¢ lower`}>
        ‹
      </button>
      {format === "cents" ? (
        <label className="flex items-baseline">
          <span className="sr-only">{label}</span>
          <input
            inputMode="numeric"
            value={text}
            disabled={disabled}
            onChange={(e) => {
              setText(e.target.value);
              const n = parseWholeCents(e.target.value);
              if (n != null && n >= 2 && n <= 98) onChange(n);
            }}
            onBlur={() => setText(String(cents))}
            className={`nums w-7 bg-transparent text-right font-semibold text-ink outline-none disabled:text-faint ${size}`}
          />
          <span className="text-[12px] text-dim">¢</span>
        </label>
      ) : (
        <span className={`nums min-w-[3.25rem] text-center font-semibold text-ink ${size}`} aria-label={label} aria-live="polite">
          {fmtBid(cents, format)}
        </span>
      )}
      <button type="button" className={step} disabled={disabled || cents >= 98} onClick={() => onChange(cents + 1)} aria-label={`${label}: 1¢ higher`}>
        ›
      </button>
    </div>
  );
}

/** Whole contracts with − / + (commits valid values as they're typed). */
export function ContractsInput({ value, onChange, label, compact = false }: { value: number; onChange: (n: number) => void; label: string; compact?: boolean }) {
  const [text, setText] = useNumberText(value, parseContracts);
  const bad = parseContracts(text) == null;
  const step = `grid h-11 w-9 shrink-0 place-items-center rounded-md text-[15px] text-dim enabled:hover:bg-raised enabled:hover:text-ink disabled:opacity-40 ${compact ? "lg:h-7 lg:w-7" : "lg:h-8 lg:w-8"}`;
  return (
    <div className={`inline-flex items-center rounded-md border bg-bg ${bad ? "border-down/60" : "border-border-2"}`}>
      <button type="button" className={step} disabled={value <= 1} onClick={() => onChange(Math.max(1, value - 1))} aria-label={`${label}: one fewer`}>
        −
      </button>
      <input
        inputMode="numeric"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          const n = parseContracts(e.target.value);
          if (n != null) onChange(n);
        }}
        onBlur={() => setText(String(value))}
        aria-label={label}
        className={`nums w-12 bg-transparent text-center font-semibold text-ink outline-none ${compact ? "text-[13px]" : "text-[15px]"}`}
      />
      <button type="button" className={step} disabled={value >= 100_000} onClick={() => onChange(value + 1)} aria-label={`${label}: one more`}>
        +
      </button>
    </div>
  );
}

/* ============================================================ confirm + result */

export interface ConfirmLine {
  key: string;
  what: ReactNode;
  venue: string;
  cents: number;
  amount: number;
  type: "limit" | "market";
  /** Contracts, when the line was sized in contracts. */
  contracts?: number;
}

/**
 * The explicit real-money confirm: every order listed (and any cancels first), the total, and
 * one Confirm button. Nothing is sent until Confirm.
 */
export function ConfirmOrders({
  lines,
  cancels = 0,
  ccy,
  busy,
  onConfirm,
  onBack,
}: {
  lines: ConfirmLine[];
  cancels?: number;
  ccy: Ccy;
  busy: boolean;
  onConfirm: () => void;
  onBack: () => void;
}) {
  const { format } = useOddsFormat();
  const total = lines.reduce((n, l) => n + l.amount, 0);
  return (
    <div className="gb-fade rounded-lg border border-down/40 bg-down/5 p-3" role="alertdialog" aria-label="Confirm real-money orders">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-down">Real money</div>
      <p className="mt-1 text-[13px] text-ink">
        {lines.length === 1 ? "This order uses" : `These ${lines.length} orders use`} real money at your connected venues.
        {cancels > 0 ? ` ${cancels} resting order${cancels === 1 ? " is" : "s are"} cancelled first.` : ""}
      </p>
      <ul className="mt-2 flex flex-col divide-y divide-border/60 rounded-md border border-border/60 bg-bg">
        {lines.map((l) => (
          <li key={l.key} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-3 py-2 text-[13px]">
            <span className="min-w-0 flex-1 text-ink">{l.what}</span>
            <span className="text-[12px] text-dim">{l.venue}</span>
            <span className="nums text-[12px] text-dim">
              {l.contracts != null ? `${fmtContracts(l.contracts)} × ` : ""}
              {l.type === "limit" ? "limit" : "market, max"} {fmtBid(l.cents, format)}
            </span>
            <span className="nums text-ink">{money(l.amount, ccy)}</span>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="nums text-[12px] text-dim">Total if everything fills: {money(total, ccy)}</span>
        <button type="button" onClick={onBack} disabled={busy} className={`${btnBase} ${btnIdle} ml-auto`}>
          Back
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className={`${btnBase} border-down/60 bg-down/15 font-semibold text-down enabled:hover:bg-down/25`}
        >
          {busy ? "Placing…" : `Confirm ${lines.length} real order${lines.length === 1 ? "" : "s"}`}
        </button>
      </div>
    </div>
  );
}

const OUTCOME_TONE: Record<PlacedLeg["outcome"], string> = {
  resting: "bg-amber/15 text-amber",
  filled: "bg-up/15 text-up",
  partial: "bg-up/10 text-up",
  pending: "bg-faint/15 text-dim",
  failed: "bg-down/15 text-down",
};
const OUTCOME_LABEL: Record<PlacedLeg["outcome"], string> = {
  resting: "resting",
  filled: "filled",
  partial: "part filled",
  pending: "pending",
  failed: "failed",
};

/** What came back for each leg: resting / filled / failed with the reason. */
export function PlacedResult({ legs, ccy, note, onDismiss }: { legs: PlacedLeg[]; ccy: Ccy; note?: ReactNode; onDismiss?: () => void }) {
  const { format } = useOddsFormat();
  const failed = legs.filter((l) => l.outcome === "failed").length;
  return (
    <div className="gb-fade rounded-md border border-border bg-bg p-3" aria-live="polite">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-dim">Just placed</span>
        <span className="nums text-[11px] text-faint">
          {legs.length - failed}/{legs.length} accepted
        </span>
        {onDismiss && (
          <button type="button" onClick={onDismiss} className="ml-auto text-[11px] text-faint hover:text-ink" aria-label="Dismiss">
            ✕
          </button>
        )}
      </div>
      <ul className="mt-2 flex flex-col gap-1.5">
        {legs.map((l, i) => (
          <li key={i} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[12px]">
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${OUTCOME_TONE[l.outcome]}`}>{OUTCOME_LABEL[l.outcome]}</span>
            <span className="min-w-0 flex-1 text-ink">
              {l.label} · {l.ref.partnerName}
              {l.cents != null && <span className="nums text-dim"> · {fmtBid(l.cents, format)}</span>}
              <span className="nums text-dim"> · {money(l.amount, ccy)}</span>
            </span>
            {l.reason && <span className="basis-full pl-1 text-down">{l.reason}</span>}
          </li>
        ))}
      </ul>
      {note && <div className="mt-2 text-[12px] text-dim">{note}</div>}
    </div>
  );
}

/** A plain message for a thrown order error (the SDK's own message, or the API's code). */
export function errorText(e: unknown): string {
  if (e && typeof e === "object") {
    const o = e as { message?: unknown; code?: unknown };
    const msg = typeof o.message === "string" ? o.message : "";
    const code = typeof o.code === "string" ? o.code : "";
    if (msg && code && !msg.includes(code)) return `${msg} (${code})`;
    if (msg) return msg;
    if (code) return code;
  }
  return String(e);
}
